import { useNavigate } from 'react-router-dom'
import {
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  MessageSquare,
  Store,
  Building2,
  RotateCcw,
  AlertCircle,
} from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { appRoutes } from '@/constants'
import { useCustomerSubmissionHistory } from '../../hooks/use-customer'

export function ApprovalHistoryPage() {
  const navigate = useNavigate()
  const { data: history, isLoading, isError, error, refetch } = useCustomerSubmissionHistory()

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Application Verification Timeline"
          title="Onboarding Approval History"
          description="Track verification progress, approval dates, and reviewer feedback notes for your Seller and Dealer applications."
          align="left"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="gap-2 shrink-0 self-start sm:self-auto text-xs font-semibold"
        >
          <RotateCcw className="size-3.5" /> Refresh Status
        </Button>
      </div>

      {/* Error state */}
      {isError && (
        <Card className="border-red-500/40 bg-red-500/10 p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <AlertCircle className="size-8 text-red-500" />
            <p className="text-sm font-bold text-foreground">Failed to load submission history</p>
            <p className="text-xs text-muted-foreground">{error?.message || 'An error occurred while fetching your history.'}</p>
            <Button size="sm" onClick={() => refetch()} className="gap-2">
              <RotateCcw className="size-3.5" /> Retry Loading
            </Button>
          </div>
        </Card>
      )}

      {/* Loading Skeleton */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} className="animate-pulse border-border/80 bg-card/60 p-6 space-y-4">
              <div className="h-6 w-1/3 rounded bg-muted/60" />
              <div className="h-4 w-2/3 rounded bg-muted/40" />
            </Card>
          ))}
        </div>
      ) : history && history.length > 0 ? (
        <div className="space-y-6">
          {history.map((item: any) => {
            const isApproved = item.status === 'APPROVED' || item.status === 'ACTIVE'
            const isPending = item.status === 'PENDING'
            const isRejected = item.status === 'REJECTED'

            return (
              <Card key={item.id} className="border-border/80 bg-card/80 backdrop-blur-xl overflow-hidden shadow-elevation-sm">
                <CardContent className="p-6 space-y-6">
                  {/* Item Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-4">
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex size-11 items-center justify-center rounded-xl ${
                          item.requestType === 'SELLER'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-purple-500/10 text-purple-500'
                        }`}
                      >
                        {item.requestType === 'SELLER' ? (
                          <Store className="size-5" />
                        ) : (
                          <Building2 className="size-5" />
                        )}
                      </span>
                      <div>
                        <h3 className="font-display text-base font-bold text-foreground">
                          {item.requestType === 'SELLER' ? 'Seller Account Request' : 'Dealer Account Request'}
                        </h3>
                        <p className="text-xs text-muted-foreground">Application Ref #{item.id}</p>
                      </div>
                    </div>

                    <Badge
                      variant={isApproved ? 'success' : isPending ? 'warning' : 'destructive'}
                      className="self-start sm:self-auto text-xs px-3 py-1 font-bold"
                    >
                      {isApproved ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="size-3.5" /> Approved
                        </span>
                      ) : isPending ? (
                        <span className="flex items-center gap-1">
                          <Clock className="size-3.5" /> Under Review
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="size-3.5" /> Rejected
                        </span>
                      )}
                    </Badge>
                  </div>

                  {/* Verification Timeline UI */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Verification Timeline
                    </h4>

                    <div className="relative pl-6 space-y-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-border/60">
                      {/* Step 1: Submitted */}
                      <div className="relative">
                        <span className="absolute -left-6 top-0.5 flex size-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-4 ring-card">
                          <CheckCircle2 className="size-3" />
                        </span>
                        <div>
                          <p className="text-xs font-bold text-foreground">Application Submitted</p>
                          <p className="text-[11px] text-muted-foreground">
                            Submission Date: {new Date(item.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Step 2: Under Review */}
                      <div className="relative">
                        <span
                          className={`absolute -left-6 top-0.5 flex size-4 items-center justify-center rounded-full text-white ring-4 ring-card ${
                            isPending
                              ? 'bg-amber-500 ring-amber-500/20'
                              : isApproved
                              ? 'bg-emerald-500'
                              : 'bg-red-500'
                          }`}
                        >
                          {isPending ? <Clock className="size-3" /> : isApproved ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                        </span>
                        <div>
                          <p className="text-xs font-bold text-foreground">
                            {isPending
                              ? 'Under Review by Platform Administrator'
                              : isApproved
                              ? 'Application Approved & Verified'
                              : 'Application Verification Declined'}
                          </p>
                          {item.reviewedAt ? (
                            <p className="text-[11px] text-muted-foreground">
                              Review Date: {new Date(item.reviewedAt).toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-[11px] text-muted-foreground">
                              In review queue.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reviewer Notes Feedback */}
                  {item.notes && (
                    <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                        <MessageSquare className="size-4 text-primary" /> Administrator Feedback & Reviewer Notes
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.notes}</p>
                    </div>
                  )}

                  {/* Action Footer */}
                  {isApproved && (
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                      <span className="font-semibold text-emerald-400">
                        Merchant status active! Sign out and log back in to be automatically redirected to your portal.
                      </span>
                    </div>
                  )}

                  {isRejected && (
                    <div className="flex justify-end gap-2 pt-2 border-t border-border/60">
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            item.requestType === 'SELLER'
                              ? appRoutes.customer.becomeSeller
                              : appRoutes.customer.becomeDealer,
                          )
                        }
                        className="gap-1.5 text-xs font-bold"
                      >
                        Re-apply Application
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card className="border-border/80 bg-card/80 p-12 text-center">
          <div className="max-w-md mx-auto space-y-3">
            <FileText className="mx-auto size-10 text-muted-foreground opacity-50" />
            <h3 className="font-display text-lg font-bold text-foreground">
              No Applications Submitted
            </h3>
            <p className="text-xs text-muted-foreground">
              You have not submitted any Seller or Dealer onboarding applications yet.
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <Button size="sm" onClick={() => navigate(appRoutes.customer.becomeSeller)} className="gap-1.5 font-bold">
                <Store className="size-4" /> Become Seller
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(appRoutes.customer.becomeDealer)}
                className="gap-1.5 font-bold text-purple-400 border-purple-500/40"
              >
                <Building2 className="size-4" /> Become Dealer
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
